import { createSyncClient, memoryStorage, type SyncClient } from '@dovefiniscono/sync';
import { SyncProvider } from '@dovefiniscono/sync/react';
import { browserCoordinator, browserLifecycle, indexedDbStorage } from '@dovefiniscono/sync/web';
import { type ReactNode, useEffect, useState } from 'react';
import { api, classifySyncError } from './api';
import { localDatabaseName, trackClient } from './local-data';
import { useSession } from './session';

export {
  useCollection,
  useStore,
  useSyncClient,
  useSyncState,
} from '@dovefiniscono/sync/react';

async function openClient(userId: string, onUnauthorized: () => void): Promise<SyncClient> {
  const name = localDatabaseName(userId);
  const options = {
    transport: api.sync,
    coordinator: browserCoordinator(name),
    lifecycle: browserLifecycle(),
    classifyError: classifySyncError,
    onUnauthorized,
  };
  try {
    return await createSyncClient({ ...options, storage: indexedDbStorage(name) });
  } catch {
    // No IndexedDB (some private modes): keep the data in memory for this visit.
    return createSyncClient({ ...options, storage: memoryStorage() });
  }
}

/** Opens the signed-in user's local database and keeps it in sync while mounted. */
export function DataProvider({ userId, children }: { userId: string; children: ReactNode }) {
  const { expire } = useSession();
  const [client, setClient] = useState<SyncClient | null>(null);

  useEffect(() => {
    let cancelled = false;
    let opened: SyncClient | null = null;
    let untrack = () => {};
    void openClient(userId, expire).then((created) => {
      if (cancelled) {
        void created.close();
        return;
      }
      opened = created;
      untrack = trackClient(userId, created);
      created.engine.start();
      setClient(created);
    });
    return () => {
      cancelled = true;
      untrack();
      setClient(null);
      void opened?.close();
    };
  }, [userId, expire]);

  if (!client) return null;
  return (
    <SyncProvider client={client} userId={userId}>
      {children}
    </SyncProvider>
  );
}
